import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Navigation, X } from "lucide-react";
import StoreLocationMap from "./StoreLocationMap";
import { captureStoreDevicePosition } from "../utils/deviceGeolocation";
import { fetchStoreLocation, saveStoreLocation } from "../services/storeLocationApi";
import { permissionService } from "../services/permissionService";
import "./StoreLocationCaptureModal.css";

const DISMISS_KEY = "naturals_partner_store_location_prompt_dismissed";
const SAVED_KEY_PREFIX = "naturals_partner_store_location_saved:";

/** Layout remounts on every partner route — only probe once per SPA session. */
let partnerLocationPromptChecked = false;

function coordsPresent(loc) {
  if (!loc || typeof loc !== "object") return false;
  if (loc.has_location) return true;
  if (loc.pending_request) return true;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function savedKeyFor(loc) {
  const fid = loc?.franchise_id ?? loc?.id;
  if (fid == null || fid === "") return `${SAVED_KEY_PREFIX}unknown`;
  return `${SAVED_KEY_PREFIX}${fid}`;
}

/**
 * After franchise partner login: prompt to capture store GPS when missing.
 * Does not re-ask once franchise GPS is saved (including org→franchise migrate).
 */
export default function StoreLocationCaptureModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("ask"); // ask | capture | done
  const [busy, setBusy] = useState(false);
  const [locking, setLocking] = useState(false);
  const [error, setError] = useState("");
  const [franchiseName, setFranchiseName] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [pinAdjusted, setPinAdjusted] = useState(false);
  const pinAdjustedRef = useRef(false);

  useEffect(() => {
    if (!permissionService.isFranchise()) return undefined;
    if (partnerLocationPromptChecked) return undefined;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") {
      partnerLocationPromptChecked = true;
      return undefined;
    }

    let cancelled = false;
    partnerLocationPromptChecked = true;
    (async () => {
      try {
        const loc = await fetchStoreLocation();
        if (cancelled) return;
        setFranchiseName(loc?.franchise_name || "");
        if (coordsPresent(loc)) {
          try {
            localStorage.setItem(savedKeyFor(loc), "1");
          } catch {
            /* ignore */
          }
          return;
        }
        try {
          if (localStorage.getItem(savedKeyFor(loc)) === "1") return;
        } catch {
          /* ignore */
        }
        setOpen(true);
        setStep("ask");
      } catch {
        // Ignore — partner can still use sidebar later.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  const applySample = (sample) => {
    if (pinAdjustedRef.current) return;
    setLatitude(sample.latitude);
    setLongitude(sample.longitude);
    setAccuracy(sample.accuracy);
  };

  const startCapture = async () => {
    setError("");
    setBusy(true);
    setLocking(true);
    setPinAdjusted(false);
    pinAdjustedRef.current = false;
    setStep("capture");
    let gotFix = false;
    try {
      const pos = await captureStoreDevicePosition((sample) => {
        gotFix = true;
        applySample(sample);
      });
      gotFix = true;
      applySample(pos);
    } catch (err) {
      setError(err?.message || "Could not get location");
      if (!gotFix) setStep("ask");
    } finally {
      setBusy(false);
      setLocking(false);
    }
  };

  const confirmSave = async () => {
    if (typeof latitude !== "number" || typeof longitude !== "number") {
      setError("Capture your location first.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const result = await saveStoreLocation({ latitude, longitude, accuracy });
      const savedLoc = result?.location || null;
      try {
        localStorage.setItem(savedKeyFor(savedLoc), "1");
      } catch {
        /* ignore */
      }
      sessionStorage.setItem(DISMISS_KEY, "1");
      setStep("done");
      window.setTimeout(() => setOpen(false), 1200);
    } catch (err) {
      setError(err?.message || "Failed to save location");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="store-loc-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="store-loc-modal-title">
      <div className="store-loc-modal-panel">
        <div className="store-loc-modal-header">
          <div className="store-loc-modal-title-row">
            <span className="store-loc-modal-icon" aria-hidden="true">
              <MapPin size={18} />
            </span>
            <h2 id="store-loc-modal-title">Set your salon location</h2>
          </div>
          <button type="button" onClick={dismiss} className="store-loc-modal-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="store-loc-modal-body">
          {step === "ask" ? (
            <>
              <p className="store-loc-modal-lead">
                {franchiseName ? (
                  <>
                    Welcome to <strong>{franchiseName}</strong>.
                  </>
                ) : (
                  "Welcome."
                )}{" "}
                For accurate salon mapping and HQ analytics, please capture GPS{" "}
                <strong>while you are at the salon</strong>. You will not be asked again after it
                is saved.
              </p>
              <div className="store-loc-modal-callout">
                Are you currently at the salon right now?
              </div>
              {error ? <p className="store-loc-modal-error">{error}</p> : null}
              <div className="store-loc-modal-actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={startCapture}
                  className="store-loc-modal-btn store-loc-modal-btn--primary"
                >
                  <Navigation size={16} />
                  {busy ? "Getting location…" : "Yes — capture now"}
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="store-loc-modal-btn store-loc-modal-btn--ghost"
                >
                  Not now
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  dismiss();
                  navigate("/nyafaz-ep/partner/store-location");
                }}
                className="store-loc-modal-link"
              >
                Open Salon location from sidebar later
              </button>
            </>
          ) : null}

          {step === "capture" ? (
            <>
              <p className="store-loc-modal-lead">
                {locking
                  ? "Capturing device location the same way as salon analysis…"
                  : "Pin is your device location. Drag to fine-tune if needed, then save."}
              </p>
              <div className="store-loc-modal-map">
                {typeof latitude === "number" && typeof longitude === "number" ? (
                  <StoreLocationMap
                    latitude={latitude}
                    longitude={longitude}
                    interactive={!locking}
                    heightClass="h-[220px]"
                    onChange={({ latitude: lat, longitude: lng }) => {
                      pinAdjustedRef.current = true;
                      setPinAdjusted(true);
                      setLatitude(lat);
                      setLongitude(lng);
                      setAccuracy(null);
                    }}
                  />
                ) : (
                  <p className="store-loc-modal-coords">Waiting for GPS…</p>
                )}
              </div>
              <p className="store-loc-modal-coords">
                {typeof latitude === "number" && typeof longitude === "number"
                  ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
                  : "—"}
                {typeof accuracy === "number" ? ` · ±${Math.round(accuracy)}m` : ""}
                {locking ? " · improving…" : ""}
              </p>
              {!locking &&
              typeof accuracy === "number" &&
              accuracy > 1000 &&
              !pinAdjusted ? (
                <p className="store-loc-modal-hint">
                  Accuracy is ±{Math.round(accuracy)} m. You can save this pin or drag it onto the
                  store.
                </p>
              ) : null}
              {error ? <p className="store-loc-modal-error">{error}</p> : null}
              <div className="store-loc-modal-actions store-loc-modal-actions--split">
                <button
                  type="button"
                  disabled={busy}
                  onClick={startCapture}
                  className="store-loc-modal-btn store-loc-modal-btn--ghost"
                >
                  Recapture
                </button>
                <button
                  type="button"
                  disabled={
                    busy ||
                    locking ||
                    typeof latitude !== "number" ||
                    typeof longitude !== "number"
                  }
                  onClick={confirmSave}
                  className="store-loc-modal-btn store-loc-modal-btn--primary"
                >
                  {busy ? "Saving…" : locking ? "Waiting for GPS…" : "Save salon location"}
                </button>
              </div>
            </>
          ) : null}

          {step === "done" ? (
            <p className="store-loc-modal-success">Salon location saved. Thank you!</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
